/**
 * Data Transformation Utilities for Communication History
 *
 * These utilities transform backend API responses into standardized frontend types,
 * handling different backend response formats and normalizing data structures.
 *
 * @module transform-communication-data
 */

import type {
  CallHistoryItem,
  BackendCallRecord,
  BackendCallRecordSingle,
  SmsHistoryItem,
  BackendSmsRecord,
  CallHistoryResponse,
  SmsHistoryResponse,
  CallStatus,
  CallType,
  CallOutcome,
  SmsStatus,
  CommunicationDirection,
} from '../types/communication-history.types';

// ============================================================================
// CALL HISTORY TRANSFORMATIONS
// ============================================================================

/**
 * Transform a backend call record (full format) to unified CallHistoryItem
 *
 * @param apiCall - Backend call record from GET /communication/calls
 * @returns Standardized CallHistoryItem
 */
export function transformBackendCallRecordFull(
  apiCall: BackendCallRecord | any
): CallHistoryItem {
  return {
    // IDs and associations
    id: apiCall.id,
    userId: apiCall.userId || '',
    agencyId: apiCall.agencyId || '',
    leadId: apiCall.leadId || null,
    contactId: apiCall.contactId || '',

    // Contact information
    phoneNumber: apiCall.phoneNumber || '',
    contactName: apiCall.contactName || apiCall.contact?.name || null,

    // Call details - handle both camelCase and database format
    callType: apiCall.callType || 'incoming',
    status: apiCall.callStatus || apiCall.status || 'missed',
    duration: apiCall.duration || 0,
    callDate: apiCall.callDate || apiCall.createdAt,

    // Phone numbers
    fromNumber: apiCall.fromNumber || apiCall.phoneNumber || '',
    toNumber: apiCall.toNumber || apiCall.phoneNumber || '',

    // Call metadata
    notes: apiCall.notes || null,
    outcome: parseCallOutcome(apiCall.outcome),
    followUpRequired: apiCall.followUpRequired || false,
    followUpDate: apiCall.followUpDate || null,
    leadStatus: apiCall.leadStatus || null,

    // Recording and events
    recordingUrl: apiCall.recordingUrl || null,
    endedAt: apiCall.endedAt || null,
    insuraABotKeySequences: apiCall.insuraABotKeySequences || null,
    callEvents: apiCall.callEvents || null,

    // How the call was placed: manual / power_dialer / multi_line (from backend)
    dialerMode: apiCall.dialerMode || undefined,

    // Timestamps
    createdAt: apiCall.createdAt,
    updatedAt: apiCall.updatedAt || apiCall.createdAt,
    deletedAt: apiCall.deletedAt || null,

    // Associated data
    contact: apiCall.contact,
  };
}

/**
 * Transform a backend call record (single format) to unified CallHistoryItem
 *
 * @param apiCall - Backend call record from GET /communication/calls/:id
 * @returns Standardized CallHistoryItem
 */
export function transformBackendCallRecordSingle(
  apiCall: BackendCallRecordSingle
): CallHistoryItem {
  // Determine call type based on from/to numbers
  const callType: CallType =
    apiCall.from === 'Unknown' || !apiCall.from ? 'incoming' : 'outgoing';

  return {
    // IDs and associations
    id: apiCall.id,
    userId: apiCall.user_id || '',
    agencyId: '', // Not available in single format
    leadId: apiCall.lead_id,
    contactId: '', // Not available in single format

    // Contact information
    phoneNumber: apiCall.to,
    contactName: null, // Not available in single format

    // Call details
    callType,
    status: apiCall.status,
    duration: apiCall.duration,
    callDate: apiCall.created_at,

    // Phone numbers
    fromNumber: apiCall.from,
    toNumber: apiCall.to,

    // Call metadata
    notes: apiCall.notes,
    outcome: parseCallOutcome(apiCall.outcome),
    followUpRequired: apiCall.follow_up_required,
    followUpDate: apiCall.follow_up_date,
    leadStatus: apiCall.lead_status,

    // Recording and events
    recordingUrl: apiCall.recording_url,
    endedAt: apiCall.ended_at,
    insuraABotKeySequences: apiCall.dtmf_sequences,
    callEvents: apiCall.call_events,

    // Timestamps
    createdAt: apiCall.created_at,
    updatedAt: apiCall.created_at, // Not available separately
    deletedAt: null,

    // Associated data
    contact: undefined,
  };
}

/**
 * Transform any backend call record format to unified CallHistoryItem
 *
 * @param apiCall - Backend call record in any format
 * @returns Standardized CallHistoryItem
 */
export function transformCallRecord(
  apiCall: BackendCallRecord | BackendCallRecordSingle
): CallHistoryItem {
  // Check which format we have by looking for callType property
  if ('callType' in apiCall) {
    return transformBackendCallRecordFull(apiCall);
  } else {
    return transformBackendCallRecordSingle(apiCall);
  }
}

/**
 * Transform a call history API response to an array of CallHistoryItems
 *
 * @param response - Backend API response
 * @returns Array of standardized CallHistoryItems
 */
export function transformCallHistoryResponse(
  response: CallHistoryResponse | { items: BackendCallRecord[] }
): CallHistoryItem[] {
  if (!response.items || !Array.isArray(response.items)) {
    return [];
  }

  return response.items.map(transformBackendCallRecordFull);
}

/**
 * Parse call outcome string to typed CallOutcome
 *
 * @param outcome - Outcome string from backend
 * @returns Typed CallOutcome or null
 */
function parseCallOutcome(outcome: string | null): CallOutcome {
  if (!outcome) return null;

  const validOutcomes: CallOutcome[] = [
    'voicemail',
    'interested',
    'not_interested',
    'callback',
    'no_answer',
    'wrong_number',
    'do_not_call',
  ];

  return validOutcomes.includes(outcome as CallOutcome)
    ? (outcome as CallOutcome)
    : null;
}

// ============================================================================
// SMS HISTORY TRANSFORMATIONS
// ============================================================================

/**
 * Transform a backend SMS record to unified SmsHistoryItem
 *
 * @param apiSms - Backend SMS record
 * @returns Standardized SmsHistoryItem
 */
export function transformBackendSmsRecord(
  apiSms: BackendSmsRecord
): SmsHistoryItem {
  // Map smsType to direction
  const direction: CommunicationDirection =
    apiSms.smsType === 'inbound' ? 'inbound' : 'outbound';

  return {
    // IDs and associations
    id: apiSms.id,
    userId: apiSms.userId,
    agencyId: apiSms.agencyId,
    leadId: apiSms.leadId,
    contactId: apiSms.contactId,

    // Contact information
    phoneNumber: apiSms.phoneNumber,
    contactName: apiSms.contactName,

    // SMS details
    direction,
    message: apiSms.message,
    status: apiSms.smsStatus,

    // Phone numbers
    fromNumber: apiSms.fromNumber,
    toNumber: apiSms.toNumber,

    // Twilio metadata
    messageSid: apiSms.messageSid,
    errorCode: apiSms.errorCode,
    errorMessage: apiSms.errorMessage,

    // Timestamps
    timestamp: apiSms.createdAt, // Use createdAt as timestamp for consistency
    sentAt: apiSms.sentAt,
    deliveredAt: apiSms.deliveredAt,
    createdAt: apiSms.createdAt,
    updatedAt: apiSms.updatedAt,
    deletedAt: apiSms.deletedAt,

    // Associated data
    contact: apiSms.contact,
    lead: apiSms.lead,
    user: apiSms.user,
  };
}

/**
 * Transform an SMS history API response to an array of SmsHistoryItems
 *
 * @param response - Backend API response
 * @returns Array of standardized SmsHistoryItems
 */
export function transformSmsHistoryResponse(
  response: SmsHistoryResponse | { items: BackendSmsRecord[] }
): SmsHistoryItem[] {
  if (!response.items || !Array.isArray(response.items)) {
    return [];
  }

  return response.items.map(transformBackendSmsRecord);
}

// ============================================================================
// LEGACY COMPATIBILITY TRANSFORMATIONS
// ============================================================================

/**
 * Transform CallHistoryItem to legacy CallRecord format
 * Used for components that haven't been updated yet
 *
 * @param item - Unified CallHistoryItem
 * @returns Legacy CallRecord
 * @deprecated Update components to use CallHistoryItem directly
 */
export function toLegacyCallRecord(item: CallHistoryItem) {
  return {
    id: item.id,
    phoneNumber: item.phoneNumber,
    callType: item.callType === 'incoming' ? ('inbound' as const) : ('outbound' as const),
    callStatus: item.status,
    duration: item.duration,
    callDate: item.callDate,
    notes: item.notes || undefined,
    recordingUrl: item.recordingUrl || undefined,
    outcome: item.outcome || undefined,
    followUpRequired: item.followUpRequired || undefined,
    followUpDate: item.followUpDate || undefined,
    contactName: item.contactName || undefined,
  };
}

/**
 * Transform SmsHistoryItem to legacy SMSRecord format
 * Used for components that haven't been updated yet
 *
 * @param item - Unified SmsHistoryItem
 * @returns Legacy SMSRecord
 * @deprecated Update components to use SmsHistoryItem directly
 */
export function toLegacySmsRecord(item: SmsHistoryItem) {
  return {
    id: item.id,
    phoneNumber: item.phoneNumber,
    contactName: item.contactName || undefined,
    direction: item.direction,
    message: item.message,
    status: item.status as 'sent' | 'delivered' | 'failed' | 'pending',
    timestamp: item.timestamp,
  };
}

// ============================================================================
// FRONTEND DISPLAY TRANSFORMATIONS
// ============================================================================

/**
 * Determine call direction based on from/to numbers and user context
 * This helper needs access to user's phone numbers to properly detect direction
 */
function determineCallDirection(
  fromNumber: string,
  toNumber: string,
  callType?: CallType
): 'inbound' | 'outbound' {
  // If callType is explicitly set and reliable, use it
  // Handle both 'incoming'/'outgoing' (backend enum) and 'inbound'/'outbound' formats
  if (callType === 'incoming' || callType === 'inbound') return 'inbound';
  if (callType === 'outgoing' || callType === 'outbound') return 'outbound';

  // Fallback: try to determine from phone numbers
  // In a real implementation, we'd check if fromNumber matches user's phone numbers
  // For now, use the callType hint or default to outbound
  return callType === 'incoming' || callType === 'inbound' ? 'inbound' : 'outbound';
}

/**
 * Determine proper call status based on various data points
 * Handles Twilio statuses and voicemail detection
 *
 * Exported so every call-list surface derives the SAME display status from the
 * same rules. The lead History tab used to render `call_status` raw, which is
 * 'completed' for voicemails — so answered-machine calls showed as "Answered"
 * and the "Voicemail" filter matched nothing. See PRIORITY 1 below for why the
 * outcome check has to win over duration/status.
 */
export function determineCallStatus(
  item: CallHistoryItem,
  direction: 'inbound' | 'outbound'
): CallHistoryItem['status'] {
  // Get the raw status from backend (may include Twilio statuses like 'completed')
  const rawStatus = item.status as string;

  // PRIORITY 1: Check for voicemail (outcome explicitly set by backend)
  // This must come before the answered check because voicemails can have duration > 0
  // (the duration of the recorded message) and status='completed'
  if (item.outcome === 'voicemail') {
    return 'voicemail';
  }
  if (direction === 'inbound' && item.recordingUrl && item.duration === 0) {
    return 'voicemail';
  }

  // PRIORITY 2: If call was completed/answered with duration > 0, it's answered
  if ((rawStatus === 'completed' || rawStatus === 'answered') && item.duration > 0) {
    return 'answered';
  }

  // Map Twilio statuses to our display statuses based on direction
  if (rawStatus === 'completed' || rawStatus === 'answered') {
    // OUTBOUND completed = always answered (user made the call)
    if (direction === 'outbound') {
      return 'answered';
    }

    // INBOUND completed with 0 duration = missed (handled above if has recording)
    return 'missed';
  }

  if (rawStatus === 'no-answer' || rawStatus === 'no_answer') {
    // Inbound no-answer = missed (someone called you, you didn't pick up)
    // Outbound no-answer = no answer (you called them, they didn't pick up)
    return direction === 'inbound' ? 'missed' : 'no-answer';
  }

  if (rawStatus === 'canceled') {
    // Inbound canceled = missed, outbound canceled = declined
    return direction === 'inbound' ? 'missed' : 'declined';
  }

  if (rawStatus === 'busy') {
    return 'busy';
  }

  if (rawStatus === 'failed') {
    return 'failed';
  }

  // If status is already one of our display statuses, use it
  if (['answered', 'missed', 'declined', 'voicemail', 'busy', 'failed'].includes(rawStatus)) {
    return rawStatus as CallHistoryItem['status'];
  }

  // Default fallback - assume answered if there's duration
  // Outbound 0-duration = no answer (they didn't pick up), inbound 0-duration = missed (you didn't pick up)
  if (item.duration > 0) return 'answered';
  return direction === 'outbound' ? 'no-answer' : 'missed';
}

/**
 * Transform CallHistoryItem for frontend display (simplified format)
 * Used by components that need a minimal call representation
 *
 * @param item - Unified CallHistoryItem
 * @returns Simplified call record for display
 */
export function toDisplayCallRecord(item: CallHistoryItem) {
  // Determine direction from fromNumber and toNumber
  const direction = determineCallDirection(item.fromNumber, item.toNumber, item.callType);

  // Determine proper status with voicemail detection
  const status = determineCallStatus(item, direction);

  return {
    id: item.id,
    phoneNumber: item.phoneNumber,
    contactName: item.contactName || null,
    direction,
    status,
    duration: item.duration || 0,
    timestamp: new Date(item.callDate),
    notes: item.notes || undefined,
    recordingUrl: item.recordingUrl || undefined,
    fromNumber: item.fromNumber,
    toNumber: item.toNumber,
    userId: item.userId,
    leadId: item.leadId,
    outcome: item.outcome,
    dialerMode: item.dialerMode,
  };
}

/**
 * Transform SmsHistoryItem for frontend display (simplified format)
 * Used by components that need a minimal SMS representation
 *
 * @param item - Unified SmsHistoryItem
 * @returns Simplified SMS record for display
 */
export function toDisplaySmsRecord(item: SmsHistoryItem) {
  return {
    id: item.id,
    phoneNumber: item.phoneNumber,
    contactName: item.contactName || null,
    direction: item.direction,
    message: item.message,
    status: item.status as 'sent' | 'delivered' | 'failed' | 'pending',
    timestamp: new Date(item.timestamp),
  };
}

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

/**
 * Validate if a call status is valid
 *
 * @param status - Status string to validate
 * @returns True if status is valid
 */
export function isValidCallStatus(status: string): status is CallStatus {
  const validStatuses: CallStatus[] = [
    'answered',
    'missed',
    'declined',
    'failed',
    'busy',
    'voicemail',
    'no-answer',
    'canceled',
  ];
  return validStatuses.includes(status as CallStatus);
}

/**
 * Validate if an SMS status is valid
 *
 * @param status - Status string to validate
 * @returns True if status is valid
 */
export function isValidSmsStatus(status: string): status is SmsStatus {
  const validStatuses: SmsStatus[] = [
    'sent',
    'delivered',
    'failed',
    'pending',
    'queued',
    'sending',
    'undelivered',
    'canceled',
  ];
  return validStatuses.includes(status as SmsStatus);
}

/**
 * Normalize phone number for consistent comparison
 *
 * @param phoneNumber - Phone number to normalize
 * @returns Normalized phone number (digits only)
 */
export function normalizePhoneNumber(phoneNumber: string): string {
  return phoneNumber.replace(/\D/g, '');
}
